import { Controller } from "@hotwired/stimulus"

import { DEFAULT_FRAME_TIMEOUT_MS, armFrameLoadTimeout } from "./frame_load_timeout"

const TAB_CONTROL_SELECTOR = "[data-railsui-tabs-target='tab'][data-url]"
const LOADING_FRAME_CLASS =
  "min-h-[320px] animate-pulse rounded-lg bg-[var(--ds-background-secondary-surface)]"
const TAB_NAV_LOADING_CLASS = "cursor-progress"
const TAB_FRAME_RETRY_SELECTOR = "[data-educator-home-tab-frame-retry]"

export default class extends Controller {
  static targets = ["frame"]

  static values = {
    activeTab: String,
    cacheKey: String,
    timeoutMs: { type: Number, default: DEFAULT_FRAME_TIMEOUT_MS },
  }

  connect() {
    this.loadingTabKey = null
    this.frameLoadPromises = new Map()
    this.activeLoadCleanups = new Map()
    this.navigationLoadingCount = 0
    this.panelBusyCounts = new Map()

    this.boundPopState = this.onPopState.bind(this)
    this.boundOnRetry = this.onRetry.bind(this)
    window.addEventListener("popstate", this.boundPopState)
    this.element.addEventListener("click", this.boundOnRetry)

    this.desktopTabs = this.element.querySelector("[data-educator-home-desktop-tabs]")
    if (this.desktopTabs) {
      this.boundTabClick = this.onTabClick.bind(this)
      this.desktopTabs.addEventListener("click", this.boundTabClick, true)
      this.scheduleBackgroundPreload()
    }

    this.frameTargets.forEach((frame) => this.resetFrameIfStale(frame))
  }

  disconnect() {
    for (const cleanup of this.activeLoadCleanups.values()) {
      cleanup()
    }
    this.activeLoadCleanups.clear()
    window.removeEventListener("popstate", this.boundPopState)
    this.element.removeEventListener("click", this.boundOnRetry)
    this.desktopTabs?.removeEventListener("click", this.boundTabClick, true)
    this.panelBusyCounts.forEach((_count, panel) => {
      panel.removeAttribute("aria-busy")
    })
    this.panelBusyCounts.clear()
    if (this.navigationLoadingCount > 0) {
      this.navigationLoadingCount = 0
      this.setTabLoadingCursor(false)
    }
  }

  scheduleBackgroundPreload() {
    if (!this.shouldBackgroundPreload()) return

    const run = () => this.preloadInactiveTabs()
    window.setTimeout(() => {
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(run, { timeout: 3000 })
      } else {
        window.setTimeout(run, 500)
      }
    }, 0)
  }

  shouldBackgroundPreload() {
    if (typeof window.matchMedia === "function" && !window.matchMedia("(min-width: 1024px)").matches) {
      return false
    }

    if (navigator.connection?.saveData) {
      return false
    }

    return true
  }

  resetFrameIfStale(frame) {
    if (frame.dataset.loaded === "true" && frame.dataset.cacheKey === this.cacheKeyValue) {
      return
    }

    if (frame.dataset.tabKey !== this.activeTabValue) {
      frame.removeAttribute("src")
      frame.replaceChildren()
      frame.dataset.loaded = "false"
    }
    frame.dataset.cacheKey = this.cacheKeyValue
  }

  preloadInactiveTabs() {
    this.frameTargets.forEach((frame) => {
      if (frame.dataset.tabKey === this.activeTabValue) return
      if (frame.dataset.loaded === "true") return
      this.loadFrame(frame, { userInitiated: false, showLoading: false })
    })
  }

  loadFrame(frame, { userInitiated = false, showLoading = false } = {}) {
    const url = frame.dataset.preloadUrl
    if (!url) return Promise.reject(new Error("missing preload url"))

    if (frame.dataset.loaded === "true" && frame.dataset.cacheKey === this.cacheKeyValue) {
      return Promise.resolve()
    }

    const tabKey = frame.dataset.tabKey
    const inFlight = tabKey ? this.frameLoadPromises.get(tabKey) : undefined
    if (inFlight) {
      if (userInitiated) {
        frame.dataset.pendingUserLoad = "true"
        if (showLoading) frame.dataset.pendingUserShowLoading = "true"
        this.beginNavigationLoading(frame)
        if (showLoading) {
          this.showLoadingPlaceholder(frame)
        }
      }
      return inFlight
    }

    if (showLoading) {
      this.showLoadingPlaceholder(frame)
    }

    const requestCacheKey = this.cacheKeyValue
    const loadPromise = new Promise((resolve, reject) => {
      let clearFrameLoadTimeout = null

      const finish = (ok, error) => {
        if (frame.dataset.loadSettled === "true") return

        const stale =
          frame.dataset.preloadUrl !== url ||
          requestCacheKey !== this.cacheKeyValue
        if (stale) {
          const reloadUserInitiated =
            userInitiated || frame.dataset.pendingUserLoad === "true"
          const reloadShowLoading =
            showLoading || frame.dataset.pendingUserShowLoading === "true"
          const shouldEndNavLoading =
            reloadUserInitiated || frame.dataset.pendingUserLoad === "true"
          cleanup()
          if (shouldEndNavLoading) {
            this.endNavigationLoading(frame)
          }
          frame.dataset.loaded = "false"
          if (tabKey && this.frameLoadPromises.get(tabKey) === loadPromise) {
            this.frameLoadPromises.delete(tabKey)
          }
          Promise.resolve().then(() => {
            this.loadFrame(frame, {
              userInitiated: reloadUserInitiated,
              showLoading: reloadShowLoading,
            }).then(resolve, reject)
          })
          return
        }

        frame.dataset.loadSettled = "true"

        const shouldEndNavLoading =
          userInitiated || frame.dataset.pendingUserLoad === "true"
        cleanup()
        if (shouldEndNavLoading) {
          this.endNavigationLoading(frame)
        }
        frame.removeAttribute("inert")
        if (ok) {
          frame.dataset.loaded = "true"
          frame.dataset.cacheKey = this.cacheKeyValue
          frame.classList.remove(...LOADING_FRAME_CLASS.split(" "))
          frame.removeAttribute("aria-busy")
          resolve()
        } else {
          reject(error ?? new Error("frame load failed"))
        }
      }

      const fail = (error) => {
        const wrappedError =
          error instanceof Error
            ? error
            : new Error(error?.message ?? "frame load failed")

        if (error?.event) {
          void this.applyServerFrameError(frame, error.event).then((applied) => {
            if (!applied) this.showFrameLoadError(frame)
            finish(false, wrappedError)
          })
          return
        }

        this.showFrameLoadError(frame)
        finish(false, wrappedError)
      }

      const onLoad = (event) => {
        if (event.target !== frame) return
        finish(true)
      }

      const onMissing = (event) => {
        if (event.target !== frame) return
        event.preventDefault()
        fail({ message: "frame missing", event })
      }

      const onFetchError = (event) => {
        if (event.target !== frame) return
        event.preventDefault()
        fail({ message: "frame fetch failed", event })
      }

      const onBeforeFetchRequest = (event) => {
        if (event.target !== frame) return

        if (clearFrameLoadTimeout) clearFrameLoadTimeout()
        clearFrameLoadTimeout = armFrameLoadTimeout({
          timeoutMs: this.timeoutMsValue,
          onTimeout: () => fail(new Error("frame load timeout")),
        })
      }

      const cleanup = () => {
        if (clearFrameLoadTimeout) {
          clearFrameLoadTimeout()
          clearFrameLoadTimeout = null
        }
        frame.removeEventListener("turbo:frame-load", onLoad)
        frame.removeEventListener("turbo:frame-missing", onMissing)
        frame.removeEventListener("turbo:fetch-request-error", onFetchError)
        frame.removeEventListener("turbo:before-fetch-request", onBeforeFetchRequest)
        delete frame.dataset.pendingUserLoad
        delete frame.dataset.pendingUserShowLoading
        this.activeLoadCleanups.delete(frame)
      }

      this.activeLoadCleanups.set(frame, cleanup)

      frame.addEventListener("turbo:frame-load", onLoad)
      frame.addEventListener("turbo:frame-missing", onMissing)
      frame.addEventListener("turbo:fetch-request-error", onFetchError)
      frame.addEventListener("turbo:before-fetch-request", onBeforeFetchRequest)

      if (userInitiated) {
        frame.dataset.pendingUserLoad = "true"
        this.beginNavigationLoading(frame)
      }

      delete frame.dataset.loadSettled
      Promise.resolve().then(() => frame.setAttribute("src", url))
    })

    if (tabKey) {
      this.frameLoadPromises.set(tabKey, loadPromise)
      loadPromise
        .finally(() => {
          if (this.frameLoadPromises.get(tabKey) === loadPromise) {
            this.frameLoadPromises.delete(tabKey)
          }
        })
        .catch(() => {})
    } else {
      loadPromise.catch(() => {})
    }

    return loadPromise
  }

  async applyServerFrameError(frame, event) {
    const response = event?.detail?.fetchResponse?.response
    if (!response || typeof response.text !== "function") return false

    try {
      const html = await response.text()
      if (!this.element.isConnected) return false

      const frameId = frame.id
      if (!html || !frameId) return false

      const serverFrame = new DOMParser()
        .parseFromString(html, "text/html")
        .getElementById(frameId)
      if (!serverFrame) return false

      frame.classList.remove(...LOADING_FRAME_CLASS.split(" "))
      frame.removeAttribute("aria-busy")
      frame.removeAttribute("src")
      frame.dataset.loaded = "false"
      frame.innerHTML = serverFrame.innerHTML
      this.focusTabFrameRetry(frame)
      return true
    } catch {
      return false
    }
  }

  showFrameLoadError(frame) {
    frame.classList.remove(...LOADING_FRAME_CLASS.split(" "))
    frame.removeAttribute("aria-busy")
    frame.removeAttribute("src")
    frame.dataset.loaded = "false"
    frame.innerHTML = `
      <div class="educator-home-page-card-error educator-home-page-card-error--embedded min-h-[320px]" data-educator-home-tab-frame-error>
        <div class="educator-home-page-card-error__body">
          <div class="educator-home-page-card-error__copy" role="alert">
            <p class="educator-home-page-card-error__title">This tab could not be loaded.</p>
            <p class="educator-home-page-card-error__message">Please try again.</p>
          </div>
          <button
            type="button"
            class="btn btn-primary"
            aria-label="Try Again"
            data-educator-home-tab-frame-retry="true"
          >
            <span class="btn-label">Try Again</span>
          </button>
        </div>
      </div>
    `

    this.focusTabFrameRetry(frame)
  }

  focusTabFrameRetry(frame) {
    const retry = frame.querySelector(TAB_FRAME_RETRY_SELECTOR)
    retry?.focus()
  }

  onRetry(event) {
    const retry = event.target.closest(TAB_FRAME_RETRY_SELECTOR)
    if (!retry || !this.element.contains(retry)) return

    const frame = retry.closest("[data-educator-home-page--tabs-target='frame']")
    if (!frame) return

    event.preventDefault()
    this.loadFrame(frame, { userInitiated: true, showLoading: true }).catch(() => {})
  }

  tabControls() {
    if (!this.desktopTabs) return []
    return [...this.desktopTabs.querySelectorAll(TAB_CONTROL_SELECTOR)]
  }

  setTabLoadingCursor(active) {
    this.tabControls().forEach((tab) => {
      if (active) {
        tab.classList.remove("cursor-pointer")
        tab.classList.add(TAB_NAV_LOADING_CLASS)
      } else {
        tab.classList.remove(TAB_NAV_LOADING_CLASS)
        tab.classList.add("cursor-pointer")
      }
    })
  }

  beginNavigationLoading(frame) {
    if (!this.desktopTabs) return

    this.navigationLoadingCount += 1
    if (this.navigationLoadingCount === 1) {
      this.setTabLoadingCursor(true)
    }

    const panel = frame.closest('[role="tabpanel"]')
    if (panel) {
      this.panelBusyCounts.set(panel, (this.panelBusyCounts.get(panel) || 0) + 1)
      panel.setAttribute("aria-busy", "true")
    }
  }

  endNavigationLoading(frame) {
    if (this.navigationLoadingCount > 0) {
      this.navigationLoadingCount -= 1
    }

    if (this.navigationLoadingCount === 0) {
      this.setTabLoadingCursor(false)
    }

    const panel = frame?.closest?.('[role="tabpanel"]')
    if (!panel) return

    const nextCount = (this.panelBusyCounts.get(panel) || 1) - 1
    if (nextCount <= 0) {
      this.panelBusyCounts.delete(panel)
      panel.removeAttribute("aria-busy")
    } else {
      this.panelBusyCounts.set(panel, nextCount)
    }
  }

  showLoadingPlaceholder(frame) {
    frame.setAttribute("aria-busy", "true")

    if (frame.dataset.loaded === "false" && frame.childElementCount > 0) {
      frame.replaceChildren()
      frame.setAttribute("inert", "")
    }

    if (frame.childElementCount > 0) return

    frame.classList.add(...LOADING_FRAME_CLASS.split(" "))
  }

  onTabClick(event) {
    const tab = event.target.closest(TAB_CONTROL_SELECTOR)
    if (!tab || !this.desktopTabs?.contains(tab)) return
    if (event.target.closest("form")) return

    const tabKey = tab.dataset.tabKey
    const frame = this.frameForTabKey(tabKey)
    if (!frame) return

    const ready =
      frame.dataset.loaded === "true" && frame.dataset.cacheKey === this.cacheKeyValue

    event.preventDefault()
    event.stopImmediatePropagation()

    if (this.loadingTabKey === tabKey) {
      return
    }

    if (ready) {
      this.activateRailsuiTab(tab)
      this.syncPageState(tab.dataset.url, tabKey)
      return
    }

    this.loadingTabKey = tabKey
    this.activateRailsuiTab(tab)

    this.loadFrame(frame, { userInitiated: true, showLoading: true })
      .then(() => {
        this.syncPageState(tab.dataset.url, tabKey)
      })
      .catch(() => {})
      .finally(() => {
        this.loadingTabKey = null
      })
  }

  frameForTabKey(tabKey) {
    return this.frameTargets.find((frame) => frame.dataset.tabKey === tabKey)
  }

  activateRailsuiTab(tab) {
    const tabsRoot = tab.closest("[data-controller~='railsui-tabs']")
    if (!tabsRoot) return

    const controller = this.application.getControllerForElementAndIdentifier(
      tabsRoot,
      "railsui-tabs"
    )
    const index = Number.parseInt(tab.dataset.index ?? "0", 10)
    controller?.activateTabAt(Number.isFinite(index) ? index : 0)
  }

  applyActiveTab(tabKey) {
    this.activeTabValue = tabKey
    this.element.dataset.educatorHomeActiveTab = tabKey
    this.element.dataset.studentsModalActiveTab = tabKey
  }

  syncPageState(url, tabKey) {
    this.applyActiveTab(tabKey)
    if (url) {
      window.history.pushState({ educatorHomeTab: tabKey }, "", url)
    }
  }

  onPopState() {
    const tabKey = new URL(window.location.href).searchParams.get("tab")
    if (!tabKey) return

    const tab = this.desktopTabs?.querySelector(
      `[data-railsui-tabs-target='tab'][data-tab-key='${tabKey}']`
    )
    const frame = this.frameForTabKey(tabKey)

    if (
      tab &&
      frame?.dataset.loaded === "true" &&
      frame.dataset.cacheKey === this.cacheKeyValue
    ) {
      this.activateRailsuiTab(tab)
      this.applyActiveTab(tabKey)
      return
    }

    if (typeof Turbo !== "undefined" && Turbo.visit) {
      Turbo.visit(window.location.href)
    }
  }
}
